import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import {
  Card, Input, Button, Tag, Typography, Alert, Checkbox, DatePicker,
  Popconfirm, App as AntApp, Flex, Empty, Spin,
} from 'antd';
import { SearchOutlined, CheckCircleOutlined, SwapOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import {
  PreviaDiferimento, DiferimentoAtivo, brl, rotuloPeriodo, periodoFim, periodoValido, pedidoValido,
  mesesReconhecidos, mexeNoPassado, primeiroMesAfetado, nomeDaCompra, mesesDoTexto, valorMensal, LIMITE_BUSCA,
} from './diferimentos/logica';

// Diferir = diluir na DRE, mês a mês, uma despesa que vale por um período
// (licença de 3 anos, seguro anual). É só competência: o pagamento continua no
// Contas a Pagar e no fluxo de caixa exatamente como está.
//
// 04/10/2026: a tela diferia UM lançamento por vez, pedia a categoria (que
// agora é sempre a da própria despesa) e aceitava o mês digitado em texto. Uma
// compra em 24 parcelas — a licença Autodesk — exigia 24 operações.
//
// 🚨 O que a pessoa CONFIRMA tem que ser o que ela VIU. Três cuidados (revisão
// de 04/10/2026): resposta atrasada de uma prévia antiga é descartada
// (`pedidoCompra`); enquanto a prévia recarrega, a caixa e o botão ficam
// travados; e o Confirmar manda junto a quantidade e o total que estavam na
// tela — se o servidor achar outra coisa, recusa em vez de gravar.

const { Title, Text } = Typography;

interface Lancamento {
  id: string;
  data_competencia: string;
  descricao: string;
  fornecedor_original: string;
  categoria_contaazul: string;
  valor: number | string;
  periodo: string;
}

const corpo = (res: any) => (res?.data?.body ? JSON.parse(res.data.body) : res?.data);
const corpoDoErro = (e: any) => {
  const d = e?.response?.data;
  if (d?.body) { try { return JSON.parse(d.body); } catch { return null; } }
  return d || null;
};
const erroDe = (e: any, padrao: string) => {
  const c = corpoDoErro(e);
  return c?.error || c?.erro || padrao;
};

function DiferimentosInner() {
  const { message } = AntApp.useApp();
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<Lancamento[]>([]);
  const [buscou, setBuscou] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [selecionado, setSelecionado] = useState<Lancamento | null>(null);
  const [compra, setCompra] = useState<PreviaDiferimento | null>(null);
  const [carregandoCompra, setCarregandoCompra] = useState(false);
  const [erroCompra, setErroCompra] = useState<string | null>(null);
  const [juntarParcelas, setJuntarParcelas] = useState(true);
  const [inicio, setInicio] = useState<Dayjs | null>(null);
  const [mesesTxt, setMesesTxt] = useState('12');
  const [salvando, setSalvando] = useState(false);
  const [diferimentos, setDiferimentos] = useState<DiferimentoAtivo[]>([]);
  const [estadoAtivos, setEstadoAtivos] = useState<'carregando' | 'ok' | 'erro'>('carregando');
  const [desfazendo, setDesfazendo] = useState<string | null>(null);
  // Número do último pedido de prévia: resposta de pedido mais antigo é jogada fora.
  const pedidoCompra = useRef(0);

  const hoje = dayjs().format('YYYY/MM');
  const inicioTxt = inicio ? inicio.format('YYYY/MM') : null;
  const meses = mesesDoTexto(mesesTxt);

  useEffect(() => { carregarDiferimentos(); }, []);

  async function carregarDiferimentos() {
    setEstadoAtivos('carregando');
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/lancamentos/diferimentos`, { headers: { Authorization: token } });
      const data = corpo(res);
      if (!Array.isArray(data)) throw new Error('resposta inesperada');
      setDiferimentos(data);
      setEstadoAtivos('ok');
    } catch {
      // Falha NÃO vira "nenhuma despesa diferida": a lista fica marcada como não carregada.
      setEstadoAtivos('erro');
    }
  }

  async function pesquisar() {
    if (buscando) return;
    if (!busca.trim()) { message.info('Digite parte da descrição, do fornecedor ou o valor da despesa.'); return; }
    setBuscando(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/lancamentos/busca?q=${encodeURIComponent(busca.trim())}`, { headers: { Authorization: token } });
      const data = corpo(res);
      setResultados(Array.isArray(data) ? data : []);
      setBuscou(true);
    } catch (e) {
      setResultados([]); setBuscou(false);
      message.error(erroDe(e, 'Não consegui buscar. Tente de novo.'), 6);
    } finally {
      setBuscando(false);
    }
  }

  // Ao escolher a despesa, o servidor diz quais outras parcelas são da mesma
  // compra (mesmo fornecedor, mesma competência, "n/24" na descrição).
  async function verCompra(l: Lancamento, juntar: boolean) {
    const pedido = ++pedidoCompra.current;
    setCarregandoCompra(true);
    setErroCompra(null);
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/lancamentos/${l.id}/diferir`,
        { dry_run: true, incluir_compra: juntar }, { headers: { Authorization: token } });
      if (pedido !== pedidoCompra.current) return;
      setCompra(corpo(res));
    } catch (e) {
      if (pedido !== pedidoCompra.current) return;
      setCompra(null);
      setErroCompra(erroDe(e, 'Não consegui ler esta despesa.'));
    } finally {
      if (pedido === pedidoCompra.current) setCarregandoCompra(false);
    }
  }

  async function selecionar(l: Lancamento) {
    setSelecionado(l);
    setCompra(null);
    setJuntarParcelas(true);
    // O prazo volta ao padrão a cada despesa: em 04/10/2026, depois de diferir a
    // Autodesk em 36 meses, a despesa seguinte já abriu com 36 no campo.
    setMesesTxt('12');
    setInicio(periodoValido(l.periodo) ? dayjs(`${l.periodo.replace('/', '-')}-01`) : dayjs().startOf('month'));
    await verCompra(l, true);
  }

  function trocar() {
    pedidoCompra.current++;            // invalida a prévia que ainda estiver a caminho
    setSelecionado(null); setCompra(null); setErroCompra(null); setCarregandoCompra(false);
  }

  async function alternarJuntar(v: boolean) {
    setJuntarParcelas(v);
    if (selecionado) await verCompra(selecionado, v);
  }

  async function diferir() {
    if (!selecionado || !compra || carregandoCompra || !pedidoValido(inicioTxt, meses)) return;
    setSalvando(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/lancamentos/${selecionado.id}/diferir`, {
        periodo_inicio: inicioTxt, meses, incluir_compra: juntarParcelas,
        // o que estava na tela — o servidor recusa se achar outra compra
        conferir: { quantidade: compra.quantidade, total: compra.total },
      }, { headers: { Authorization: token } });
      const data = corpo(res);
      const oQue = data.quantidade > 1 ? `${data.quantidade} parcelas diferidas` : 'Despesa diferida';
      message.success(`${oQue}: ${brl(data.valorMensal)} por mês até ${rotuloPeriodo(data.periodoFim)}.`, 6);
      trocar(); setResultados([]); setBuscou(false); setBusca('');
    } catch (e) {
      // 409 vem com o resumo atualizado (o que mudou, qual parcela tem problema):
      // a tela passa a mostrar ISSO, e não a prévia antiga.
      const c = corpoDoErro(e);
      if (c && Array.isArray(c.lancamentos) && Array.isArray(c.problemas)) setCompra(c);
      message.error(erroDe(e, 'Não consegui diferir. Confira a lista abaixo antes de tentar de novo.'), 8);
    } finally {
      setSalvando(false);
      // Com erro de rede o servidor pode ter gravado mesmo assim: a lista é a verdade.
      await carregarDiferimentos();
    }
  }

  async function desfazer(d: DiferimentoAtivo) {
    setDesfazendo(d.id);
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/lancamentos/${d.lancamento_id}/diferir`, { headers: { Authorization: token } });
      message.success('Diferimento desfeito. A despesa voltou inteira para o mês original.', 6);
    } catch (e) {
      message.error(erroDe(e, 'Não consegui desfazer. Confira a lista antes de tentar de novo.'), 8);
    } finally {
      setDesfazendo(null);
      await carregarDiferimentos();
    }
  }

  const temIrmas = !!compra && compra.quantidade > 1;
  const pedidoOk = pedidoValido(inicioTxt, meses);
  const mensal = compra && pedidoOk ? valorMensal(compra.total, meses as number) : null;
  const fim = pedidoOk ? periodoFim(inicioTxt as string, meses as number) : null;
  const problemas = compra?.problemas || [];
  const mesesInvalido = mesesTxt.trim() !== '' && !(meses != null && meses >= 2 && meses <= 120);
  const primeiroMes = primeiroMesAfetado(inicioTxt, selecionado?.periodo);
  const podeConfirmar = !!compra && pedidoOk && problemas.length === 0 && !carregandoCompra;

  return (
    <div style={{ padding: '16px clamp(12px, 3vw, 24px)', maxWidth: 900, margin: '0 auto' }}>
      <Title level={4} style={{ marginTop: 0, marginBottom: 4, color: '#020D7F' }}>Diferimentos</Title>
      <Text type="secondary">
        Dilui na DRE, mês a mês, uma despesa que vale por um período (licença, seguro, consultoria).
        O pagamento e o fluxo de caixa não mudam.
      </Text>

      <Flex gap={8} style={{ marginTop: 16, marginBottom: 12 }}>
        <Input
          id="dif-busca"
          aria-label="Buscar a despesa por descrição, fornecedor ou valor"
          value={busca}
          onChange={e => setBusca(e.target.value)}
          onPressEnter={pesquisar}
          placeholder="Buscar a despesa por descrição, fornecedor ou valor"
          prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          allowClear
          style={{ flex: 1, minWidth: 0 }}
        />
        <Button type="primary" loading={buscando} onClick={pesquisar}>Buscar</Button>
      </Flex>

      {!selecionado && buscou && resultados.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma despesa encontrada (as já diferidas não aparecem aqui)." />
      )}

      {!selecionado && resultados.length > 0 && (
        <>
          <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { padding: 0, maxHeight: 'clamp(220px, calc(100vh - 420px), 520px)', overflowY: 'auto' } }}>
            {resultados.map((l, i) => (
              <div
                key={l.id}
                role="button"
                tabIndex={0}
                onClick={() => selecionar(l)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selecionar(l); } }}
                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: i < resultados.length - 1 ? '1px solid #F0F1F5' : 'none' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F7FA')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <Flex justify="space-between" align="flex-start" gap={12}>
                  <div style={{ minWidth: 0 }}>
                    <Text strong style={{ fontSize: 13 }}>{l.descricao || l.fornecedor_original}</Text>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      {l.fornecedor_original} · {rotuloPeriodo(l.periodo) || l.periodo} · {l.categoria_contaazul || 'sem categoria'}
                    </div>
                  </div>
                  <Text strong style={{ color: '#B91626', fontSize: 13, whiteSpace: 'nowrap' }}>{brl(Number(l.valor))}</Text>
                </Flex>
              </div>
            ))}
          </Card>
          {resultados.length >= LIMITE_BUSCA && (
            <Text type="warning" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
              Mostrando as {LIMITE_BUSCA} mais recentes. Se a despesa não está aqui, escreva mais da descrição ou o valor.
            </Text>
          )}
        </>
      )}

      {selecionado && (
        <Card size="small" style={{ borderColor: '#0930D0', marginBottom: 16 }}>
          <Flex justify="space-between" align="flex-start" gap={12} wrap="wrap" style={{ marginBottom: 12 }}>
            <div style={{ minWidth: 0, flex: '1 1 240px' }}>
              <Text strong>{selecionado.fornecedor_original || selecionado.descricao}</Text>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                {compra
                  ? <>{compra.quantidade === 1 ? '1 lançamento' : `${compra.quantidade} parcelas`} · {brl(compra.total)} · {compra.categoria_dre || 'sem categoria'}</>
                  : selecionado.descricao}
              </div>
            </div>
            <Button size="small" icon={<SwapOutlined />} onClick={trocar}>Trocar</Button>
          </Flex>

          {!compra && carregandoCompra && <Flex justify="center" style={{ padding: 24 }}><Spin /></Flex>}

          {!compra && !carregandoCompra && erroCompra && (
            <Alert type="error" showIcon message="Não consegui abrir esta despesa" description={erroCompra}
              action={<Button size="small" icon={<ReloadOutlined />} onClick={() => verCompra(selecionado, juntarParcelas)}>Tentar de novo</Button>} />
          )}

          {compra && (
            <Spin spinning={carregandoCompra}>
              {(temIrmas || !juntarParcelas) && (
                <Checkbox id="dif-juntar" checked={juntarParcelas} disabled={carregandoCompra || salvando}
                  onChange={e => alternarJuntar(e.target.checked)} style={{ marginBottom: 12 }}>
                  Diferir juntas todas as parcelas desta compra
                </Checkbox>
              )}

              {temIrmas && (
                <div style={{ maxHeight: 'clamp(96px, calc(100vh - 640px), 320px)', overflowY: 'auto', border: '1px solid #F0F1F5', borderRadius: 6, marginBottom: 12 }}>
                  {compra.lancamentos.map(p => (
                    <div key={p.id} style={{ padding: '4px 10px', fontSize: 12, borderBottom: '1px solid #F7F8FA' }}>
                      <Flex justify="space-between" gap={12}>
                        <span title={p.descricao} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.descricao}</span>
                        <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{brl(p.valor)}</span>
                      </Flex>
                      {p.problema && <Text type="danger" style={{ fontSize: 12 }}>{p.problema}</Text>}
                    </div>
                  ))}
                </div>
              )}

              {problemas.length > 0 && (
                <Alert type="error" showIcon style={{ marginBottom: 12 }}
                  message="Não dá para diferir assim"
                  description={<>{problemas.slice(0, 3).map((p, i) => <div key={i}>{p}</div>)}{problemas.length > 3 && <div>… e mais {problemas.length - 3}.</div>}</>} />
              )}

              <Flex gap={12} wrap="wrap" style={{ marginBottom: 12 }}>
                <label htmlFor="dif-inicio" style={{ fontSize: 12 }}>
                  <div style={{ marginBottom: 4 }}>Começa em</div>
                  <DatePicker id="dif-inicio" picker="month" value={inicio} onChange={v => setInicio(v)} format="MM/YYYY" allowClear={false}
                    minDate={dayjs('2015-01-01')} maxDate={dayjs('2100-12-01')} />
                </label>
                <label htmlFor="dif-meses" style={{ fontSize: 12 }}>
                  <div style={{ marginBottom: 4 }}>Diluir em quantos meses</div>
                  <Input id="dif-meses" value={mesesTxt} inputMode="numeric" maxLength={3} suffix="meses" style={{ width: 150 }}
                    status={mesesInvalido ? 'error' : undefined}
                    onChange={e => setMesesTxt(e.target.value.replace(/[^\d]/g, ''))} />
                  <div style={{ marginTop: 2, color: mesesInvalido ? '#B91626' : '#999' }}>
                    {mesesTxt.trim() === '' ? 'Informe o prazo.' : 'De 2 a 120 meses.'}
                  </div>
                </label>
              </Flex>

              {pedidoOk && mensal != null && problemas.length === 0 && (
                <Alert type="info" showIcon style={{ marginBottom: 12 }}
                  message={<span><strong>{brl(mensal)}</strong> por mês, de {rotuloPeriodo(inicioTxt)} a {rotuloPeriodo(fim)}, na linha “{compra.categoria_dre}”.</span>} />
              )}
              {pedidoOk && problemas.length === 0 && mexeNoPassado(inicioTxt, hoje, selecionado.periodo) && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                  message={`Isto muda a DRE de meses que já passaram, a partir de ${rotuloPeriodo(primeiroMes)}.`} />
              )}

              <Button type="primary" block icon={<CheckCircleOutlined />} loading={salvando} disabled={!podeConfirmar} onClick={diferir}>
                Confirmar diferimento
              </Button>
            </Spin>
          )}
        </Card>
      )}

      <Title level={5} style={{ marginTop: 20, marginBottom: 8, color: '#333' }}>Diferimentos ativos</Title>
      {estadoAtivos === 'carregando' && diferimentos.length === 0 && <Text type="secondary">Carregando…</Text>}
      {estadoAtivos === 'erro' && (
        <Alert type="error" showIcon style={{ marginBottom: 8 }}
          message="Não consegui carregar a lista de diferimentos."
          description="Isto não quer dizer que não há nenhum — só que a lista não veio."
          action={<Button size="small" icon={<ReloadOutlined />} onClick={carregarDiferimentos}>Tentar de novo</Button>} />
      )}
      {estadoAtivos === 'ok' && diferimentos.length === 0 && <Text type="secondary">Nenhuma despesa diferida.</Text>}
      {diferimentos.length > 0 && (
        <Card size="small" styles={{ body: { padding: 0 } }}>
          {diferimentos.map((d, i) => {
            const feitos = mesesReconhecidos(d, hoje);
            return (
              <Flex key={d.id} justify="space-between" align="center" gap={12} wrap="wrap"
                style={{ padding: '10px 14px', borderBottom: i < diferimentos.length - 1 ? '1px solid #F0F1F5' : 'none' }}>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <Text strong style={{ fontSize: 13 }}>{nomeDaCompra(d)}</Text>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#555' }}>
                    <Tag color="processing" style={{ marginRight: 6, fontSize: 11 }}>{d.categoria_dre}</Tag>
                    {brl(d.valor_total)} · <strong>{brl(d.valor_mensal)}</strong> por mês ·{' '}
                    {rotuloPeriodo(d.periodo_inicio)} a {rotuloPeriodo(d.periodo_fim)} · {feitos} de {d.meses} meses já na DRE
                  </div>
                </div>
                <Popconfirm
                  title="Desfazer este diferimento?"
                  description={d.quantidade > 1
                    ? `As ${d.quantidade} parcelas voltam inteiras para o mês original da despesa.`
                    : 'A despesa volta inteira para o mês original.'}
                  okText="Desfazer" cancelText="Manter" onConfirm={() => desfazer(d)}>
                  <Button size="small" danger type="text" icon={<DeleteOutlined />} loading={desfazendo === d.id}>Desfazer</Button>
                </Popconfirm>
              </Flex>
            );
          })}
        </Card>
      )}
    </div>
  );
}

export default function Diferimentos() {
  return (
    <AntApp>
      <DiferimentosInner />
    </AntApp>
  );
}
